/**
 * AppShell - Top-level layout for the new UI
 *
 * Composes: TopAppBar + BottomNavBar + HamburgerMenu + OfflineBanner + PWAInstallPrompt
 * Wraps a scrollable content area for child routes.
 */

import { useState, useEffect, type ReactNode } from 'react'
import { useNavigation, type ViewId } from '@/hooks/useNavigation'
import { OfflineBanner } from '@/components/OfflineBanner'
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt'
import { HamburgerMenu } from './HamburgerMenu'
import { BottomNavBar, type NavItem } from '@/components/ui/navigation/BottomNav'
import { TopBarContentProvider, useTopBarContentValue } from '@/contexts/TopBarContent'

interface AppShellProps {
  children: ReactNode
}

/**
 * AppShell with content injection slot in top bar.
 * Child views can use `useTopBarContent()` to inject content
 * into the top bar area (e.g. progress bar during review).
 */

const CACHE_VERSION = import.meta.env.VITE_CACHE_VERSION as string

const NAV_ITEMS: NavItem[] = [
  { id: 'generate', icon: 'auto_awesome', label: 'Generate' },
  { id: 'review', icon: 'style', label: 'Review' },
  { id: 'stats', icon: 'bar_chart', label: 'Stats' },
]

/**
 * Inner layout that reads the injected top bar content.
 * Must be rendered inside TopBarContentProvider.
 */
function AppShellInner({ children }: AppShellProps) {
  const { currentView, navigateTo } = useNavigation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const topBarContent = useTopBarContentValue()
  const showVersionBadge = window.location.hostname !== 'piccard.app'

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isMenuOpen && !(e.target as HTMLElement).closest('.hamburger-menu') && !(e.target as HTMLElement).closest('.menu-trigger')) {
        setIsMenuOpen(false)
      }
    }
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  const handleMenuNavigate = (view: ViewId) => {
    navigateTo(view)
    setIsMenuOpen(false)
  }

  return (
    <div className="flex flex-col min-h-[100dvh] bg-surface">
      {/* Top App Bar */}
      <header className="sticky top-0 z-40 bg-surface-container-lowest/95 backdrop-blur-sm shadow-ambient">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              className="menu-trigger p-2 rounded-xl hover:bg-surface-container-low transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Open menu"
              aria-expanded={isMenuOpen}
            >
              <span className="material-symbols-outlined text-on-surface">menu</span>
            </button>
            <h1 className="font-headline font-extrabold text-lg text-primary">Piccard</h1>
            {showVersionBadge && (
              <span className="text-[10px] font-mono text-on-surface-variant bg-surface-container-highest rounded px-1.5 py-0.5 leading-none select-all">
                {CACHE_VERSION}
              </span>
            )}
          </div>
        </div>
        {/* Injected content below top bar row - e.g. progress bar */}
        {topBarContent && (
          <div className="px-4 pb-2">
            {topBarContent}
          </div>
        )}
      </header>

      {/* Inline Banners below top bar */}
      <PWAInstallPrompt />
      {!isOnline && <OfflineBanner />}

      {/* Hamburger Menu */}
      <HamburgerMenu
        isOpen={isMenuOpen}
        currentView={currentView}
        onNavigate={handleMenuNavigate}
        onClose={() => setIsMenuOpen(false)}
      />

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pb-20">
        {children}
      </main>

      {/* Bottom Navigation - hidden when hamburger menu is open */}
      {!isMenuOpen && (
        <BottomNavBar
          items={NAV_ITEMS}
          active={currentView}
          onNavigate={(id) => navigateTo(id as ViewId)}
        />
      )}
    </div>
  )
}

export function AppShell({ children }: AppShellProps) {
  return (
    <TopBarContentProvider>
      <AppShellInner>{children}</AppShellInner>
    </TopBarContentProvider>
  )
}
